import { FC } from "react";

import { useAssessedCountData, useAssessmentNavData } from "../../utils/assessment";
import { useQueryParamsManager } from "../../utils/routing";

import { SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { BaseFooter } from "components/footer/BaseFooter";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { PillButton, PillButtonProps } from "mds/components/PillButton";
import { Tooltip } from "mds/components/Tooltip";
import { useIsSmOrLarger } from "mds/hooks/use-responsive";
import { ChevronLeftIcon, ChevronRightIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectCurrentCourseUserId, selectFullSubmissionById } from "store/selectors";

export type AssessmentProgressBarProps = {
  selectedPage: PageType | undefined;
};

export const AssessmentProgressBar: FC<AssessmentProgressBarProps> = ({ selectedPage }) => {
  const { onBack, onNext, prevId, isLastPanel } = useAssessmentNavData(selectedPage);
  const courseUserId = useAppSelector(selectCurrentCourseUserId);
  const { getQueryParam } = useQueryParamsManager();

  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const submission = useAppSelector((s) => selectFullSubmissionById(s, submissionId));

  const isNextDisabled = !courseUserId || isLastPanel;
  const isPrevDisabled = !courseUserId || !prevId;

  const assessedCountData = useAssessedCountData(selectedPage);
  const { count, total } = assessedCountData;

  const isMobile = !useIsSmOrLarger();

  // Early return after all hooks
  if (!selectedPage) return null;

  const percentage = total === 0 ? 0 : Math.ceil((count / total) * 100);
  const safePercentage = Math.min(100, Math.max(0, percentage));

  const submitBtnAttrs: PillButtonProps = {
    form: "assessment-grading-form",
    type: "submit",
    name: "submitSource",
  };
  const prevBtnAttrs: PillButtonProps = submission
    ? { ...submitBtnAttrs, value: "prev" }
    : { onClick: onBack };
  const nextBtnAttrs: PillButtonProps = submission
    ? { ...submitBtnAttrs, value: "next" }
    : { onClick: onNext };

  return (
    <BaseFooter>
      <div className="flex w-full items-center justify-center gap-3 text-sm font-medium text-black-tint-40">
        <PillButton
          disabled={isPrevDisabled}
          eventName="Assessment - Previous"
          kind="gray"
          size="m"
          {...prevBtnAttrs}
        >
          <IconText iconStart={<ChevronLeftIcon />} text={t("common.previous")} />
        </PillButton>

        <div className="flex w-[60px] items-center justify-end whitespace-nowrap">
          {t("common.index_of_total_counter", { index: count, total })}
        </div>

        {!isMobile && (
          <>
            <div className="flex w-full max-w-[1000px] flex-grow">
              <div className="h-3 w-full rounded-full bg-green-tint-70">
                <div
                  className="duration-400 h-3 rounded-full bg-green-shade-20 transition-[width] ease-in-out"
                  style={{ width: `${safePercentage}%` }}
                />
              </div>
            </div>

            <div className="flex w-[40px] items-center justify-start">
              {t("common.percentage", { number: safePercentage })}
            </div>
          </>
        )}

        <Tooltip
          text={!courseUserId ? t("assessment_card.cannot_assess_without_enrollment") : undefined}
        >
          <PillButton
            disabled={isNextDisabled}
            eventName="Assessment - Next"
            kind="green"
            size="m"
            {...nextBtnAttrs}
          >
            <IconText iconEnd={<ChevronRightIcon />} text={t("common.next")} />
          </PillButton>
        </Tooltip>
      </div>
    </BaseFooter>
  );
};
